import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bread } from "@/components/Bread";
import { CompletionScreen } from "@/components/CompletionScreen";
import { DayLoader } from "@/components/DayLoader";
import { ProgressBar } from "@/components/ProgressBar";
import { Pronounce } from "@/components/Pronounce";
import { useSettings } from "@/hooks/useSettings";
import { pad, pad3 } from "@/lib/format";
import { buildQuiz } from "@/lib/quiz";
import { gradeFromCorrect, review, studyQueue } from "@/lib/study";
import type { DayData } from "@/types/lex";

export function Quiz() {
  const { day: dayParam } = useParams<{ day: string }>();
  const day = Number(dayParam) || 1;
  return (
    <DayLoader day={day}>
      {(data) => <Body day={day} data={data} />}
    </DayLoader>
  );
}

function Body({ day, data }: { day: number; data: DayData }) {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [questions, setQuestions] = useState(() => buildQuiz(studyQueue(day, data.words, settings.roundSize), data.words));
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [done, setDone] = useState(false);
  const feedbackRef = useRef<HTMLDivElement>(null);

  const q = questions[i];

  // The example + Next button render below the stacked options, which on
  // phones sits past the fold. Pull it into view as soon as an answer lands
  // so the learner can read the example and advance without hunting for it.
  useEffect(() => {
    if (picked) {
      feedbackRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [picked]);

  function goNext() {
    if (i + 1 >= questions.length) setDone(true);
    else {
      setI(i + 1);
      setPicked(null);
    }
  }

  function pick(opt: string) {
    if (picked || !q) return;
    setPicked(opt);
    const right = opt === q.correct;
    review(day, q.word.index, gradeFromCorrect(right));
    if (right) setCorrect((c) => c + 1);
    else setWrong((w) => w + 1);
    if (settings.autoNext) {
      setTimeout(goNext, 1200);
    }
  }

  function restart() {
    setQuestions(buildQuiz(studyQueue(day, data.words, settings.roundSize), data.words));
    setI(0);
    setPicked(null);
    setCorrect(0);
    setWrong(0);
    setDone(false);
  }

  const accuracy = useMemo(
    () => (questions.length === 0 ? 0 : Math.round((correct / questions.length) * 100)),
    [correct, questions.length]
  );

  if (questions.length === 0) {
    return (
      <div className="page">
        <div className="container" style={{ paddingTop: "var(--s-7)" }}>
          <div className="empty">Not enough words to build a quiz on this day.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container" style={{ paddingTop: "var(--s-5)" }}>
        <Bread
          crumbs={[
            { label: "Archive", to: "/" },
            { label: `Day ${pad(day)}`, to: `/day/${day}` },
            { label: "Quiz" },
          ]}
        />
      </div>

      <section className="container" style={{ paddingTop: "var(--s-5)" }}>
        <div className="row-s" style={{ marginBottom: "var(--s-5)" }}>
          <div className="stack" style={{ gap: 4 }}>
            <div className="t-eyebrow">Mode M02 · Quiz</div>
            <h1 className="t-display" style={{ fontSize: "var(--t-2xl)", margin: 0 }}>
              Pick the <span className="italic">correct meaning</span>.
            </h1>
          </div>
          <div className="row-c" style={{ gap: 16 }}>
            <ScorePill dot="moss" value={correct} />
            <ScorePill dot="red" value={wrong} />
            <button className="btn btn-sm" onClick={() => navigate(`/day/${day}`)} type="button">Exit</button>
          </div>
        </div>
        <div className="row-c" style={{ gap: 12, marginBottom: "var(--s-6)" }}>
          <div style={{ flex: 1 }}>
            <ProgressBar value={done ? questions.length : i} max={questions.length} />
          </div>
          <div className="t-mono" style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Q{pad(i + 1)} / {pad(questions.length)}
          </div>
        </div>

        {!done && q && (
          <div className="quiz-frame">
            <div className="quiz-frame__head">
              <div className="t-mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>
                ENTRY {pad3(q.word.index)} · {q.word.pos.toUpperCase()}
              </div>
              <div className="t-mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>
                Q · {pad(i + 1)}
              </div>
            </div>
            <div className="quiz-cue">What is the meaning of</div>
            <div className="row-c" style={{ gap: 16, marginBottom: "var(--s-6)", flexWrap: "wrap" }}>
              <h2 className="quiz-prompt">{q.word.word}</h2>
              <Pronounce word={q.word.word} size={36} />
              <div className="t-mono" style={{ fontSize: 13, color: "var(--text-muted)" }}>/{q.word.ipa}/</div>
            </div>
            <div className="quiz-options">
              {q.options.map((opt, k) => {
                const isCorrect = picked != null && opt === q.correct;
                const isWrong = picked === opt && opt !== q.correct;
                return (
                  <button
                    key={k}
                    className={`quiz-option ${isCorrect ? "is-correct" : ""} ${isWrong ? "is-wrong" : ""} ${picked ? "is-disabled" : ""}`}
                    onClick={() => pick(opt)}
                    type="button"
                  >
                    <span className="quiz-option__letter">{String.fromCharCode(65 + k)}</span>
                    <span className="quiz-option__text">{opt}</span>
                  </button>
                );
              })}
            </div>
            {picked && (
              <div
                ref={feedbackRef}
                style={{
                  marginTop: "var(--s-5)",
                  paddingTop: "var(--s-4)",
                  borderTop: "var(--border-hair)",
                }}
              >
                <div className="t-mono" style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
                  EXAMPLE
                </div>
                <p className="t-display" style={{ fontSize: "var(--t-md)", margin: 0, color: "var(--text-dim)" }}>
                  “{q.word.example_en}”
                </p>
                <p
                  className="t-display italic"
                  style={{ fontSize: "var(--t-sm)", margin: "6px 0 0 0", color: "var(--text-muted)" }}
                >
                  {q.word.example_vi}
                </p>
                {!settings.autoNext && (
                  <div className="row-c" style={{ justifyContent: "flex-end", marginTop: "var(--s-4)" }}>
                    <button className="btn btn-solid btn-sm" onClick={goNext} type="button">
                      Next →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {done && (
          <CompletionScreen
            title={
              <>
                <span className="t-num">{correct}</span>
                <span style={{ color: "var(--text-muted)" }}> / </span>
                {questions.length}
              </>
            }
            subtitle={
              correct === questions.length
                ? "Perfect score."
                : correct >= questions.length * 0.7
                ? "Solid run."
                : "Keep practising."
            }
            stats={[
              { label: "Correct", value: correct, dot: "moss" },
              { label: "Wrong", value: wrong, dot: "red" },
              { label: "Accuracy", value: `${accuracy}%`, dot: "moss" },
            ]}
            primaryAction={{ label: "Back to day →", onClick: () => navigate(`/day/${day}`) }}
            secondaryAction={{ label: "↻ Try again", onClick: restart }}
          />
        )}
      </section>
    </div>
  );
}

function ScorePill({ dot, value }: { dot: "moss" | "red"; value: number }) {
  return (
    <span
      className="t-mono"
      style={{ fontSize: 12, color: dot === "moss" ? "var(--accent-moss)" : "var(--accent-red)" }}
    >
      <span className={`dot dot--${dot}`} style={{ marginRight: 6 }} />
      {value}
    </span>
  );
}
